/**
 * Minimal, deliberately safe markdown renderer for transcripts.
 *
 * Threat model: transcript text is agent output and user input, and it renders
 * in a page that holds a live control socket to the user's engine. So the rule
 * here is: **escape first, then only ever emit tags this module writes itself.**
 * No HTML passthrough, no `innerHTML` of raw model text, no `javascript:` URLs,
 * no `data:` URLs.
 *
 * Supported (the subset that actually appears in Zeron transcripts): fenced code
 * blocks, inline code, bold, italic, headings, unordered and ordered lists,
 * blockquotes, horizontal rules, links with an http/https/mailto scheme, and
 * paragraphs. Anything else renders as literal escaped text, which is the right
 * failure mode — a new markdown feature is cosmetic, a missed escape is not.
 */

export const escapeHtml = (text: string): string =>
  text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

/** Only these schemes become links. `javascript:` and `data:` are rendered as
 * literal text, which is what stops `[x](javascript:alert(1))`. */
const SAFE_SCHEME = /^(https?:|mailto:)/i;

export const safeHref = (raw: string): string | undefined => {
  const trimmed = raw.trim();
  // A scheme-relative or relative URL cannot carry script, but it can point the
  // user somewhere unexpected, so only absolute http(s)/mailto links survive.
  if (SAFE_SCHEME.test(trimmed)) return trimmed;
  return undefined;
};

/** Inline spans. Input is already HTML-escaped.
 *
 * Code spans are lifted out into placeholders first so the emphasis passes
 * cannot rewrite their contents — `\`**x**\`` must stay literal, not become
 * bold. Placeholders use a private-use character that escaping never emits. */
const CODE_OPEN = "\u0000c";
const CODE_CLOSE = "\u0000C";

const renderInline = (escaped: string): string => {
  const codes: string[] = [];
  let out = escaped.replace(/`([^`]+)`/g, (_match, code: string) => {
    codes.push(`<code>${code}</code>`);
    return `${CODE_OPEN}${codes.length - 1}${CODE_CLOSE}`;
  });
  // Links: [label](href). The href is already escaped, so it can go straight
  // into the attribute — no decode/re-encode round trip, which would
  // double-escape any `&amp;` the author actually typed. The scheme check runs
  // on the escaped form: `:` and the scheme letters are never escaped, so it
  // still sees `javascript:` and still refuses it.
  out = out.replace(/\[([^\]]*)\]\(([^)\s]+)\)/g, (_match, label: string, href: string) => {
    const safe = safeHref(href);
    if (safe === undefined) return `${label} (${href})`;
    return `<a href="${safe}" target="_blank" rel="noreferrer noopener">${label}</a>`;
  });
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/(^|[^*\w])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  out = out.replace(/(^|[\s(])_([^_\n]+)_/g, "$1<em>$2</em>");
  // Restore code spans last: nothing after this point can touch them.
  return out.replace(/\u0000c(\d+)\u0000C/g, (_match, index: string) => codes[Number(index)] ?? "");
};

/** Fence opener: three backticks then an optional info string. The info string
 * is not trusted: it becomes a class attribute only when it is a plain language
 * word, so a crafted fence cannot inject markup through it. */
const FENCE = /^```\s*(\S*)/;
const LANGUAGE = /^[A-Za-z0-9+#-]{1,20}$/;

export const renderMarkdown = (source: string): string => {
  const lines = source.replaceAll("\r\n", "\n").split("\n");
  const html: string[] = [];
  let index = 0;
  let paragraph: string[] = [];
  let listKind: "ul" | "ol" | undefined;

  const flushParagraph = (): void => {
    if (paragraph.length === 0) return;
    html.push(`<p>${renderInline(escapeHtml(paragraph.join("\n"))).replaceAll("\n", "<br />")}</p>`);
    paragraph = [];
  };
  const closeList = (): void => {
    if (listKind !== undefined) {
      html.push(`</${listKind}>`);
      listKind = undefined;
    }
  };

  while (index < lines.length) {
    const line = lines[index] ?? "";
    const fence = FENCE.exec(line);
    if (fence !== null) {
      flushParagraph();
      closeList();
      const info = fence[1] ?? "";
      const language = LANGUAGE.test(info) ? info : "";
      const body: string[] = [];
      index += 1;
      while (index < lines.length && !(lines[index] ?? "").startsWith("```")) {
        body.push(lines[index] ?? "");
        index += 1;
      }
      index += 1; // consume the closing fence (or run off the end)
      const cls = language.length > 0 ? ` class="language-${language}"` : "";
      html.push(`<pre><code${cls}>${escapeHtml(body.join("\n"))}</code></pre>`);
      continue;
    }

    if (/^\s*$/.test(line)) {
      flushParagraph();
      closeList();
      index += 1;
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading !== null) {
      flushParagraph();
      closeList();
      const level = (heading[1] ?? "#").length;
      html.push(`<h${level}>${renderInline(escapeHtml(heading[2] ?? ""))}</h${level}>`);
      index += 1;
      continue;
    }

    // Horizontal rule: three or more of the same marker, spaces allowed.
    if (/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      flushParagraph();
      closeList();
      html.push("<hr />");
      index += 1;
      continue;
    }

    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (bullet !== null || numbered !== null) {
      flushParagraph();
      const kind = bullet !== null ? "ul" : "ol";
      if (listKind !== kind) {
        closeList();
        html.push(`<${kind}>`);
        listKind = kind;
      }
      html.push(`<li>${renderInline(escapeHtml((bullet?.[1] ?? numbered?.[1]) ?? ""))}</li>`);
      index += 1;
      continue;
    }

    const quote = /^\s*>\s?(.*)$/.exec(line);
    if (quote !== null) {
      flushParagraph();
      closeList();
      const body: string[] = [quote[1] ?? ""];
      index += 1;
      while (index < lines.length && /^\s*>/.test(lines[index] ?? "")) {
        body.push((/^\s*>\s?(.*)$/.exec(lines[index] ?? "")?.[1] ?? ""));
        index += 1;
      }
      html.push(`<blockquote>${renderInline(escapeHtml(body.join("\n"))).replaceAll("\n", "<br />")}</blockquote>`);
      continue;
    }

    paragraph.push(line);
    index += 1;
  }

  flushParagraph();
  closeList();
  return html.join("");
};
